import React from "react";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";

/**
 * CGrid - A wrapper for MUI Grid component with additional functionality
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content of the grid
 * @param {number|object} props.xs - Grid width for extra small screens
 * @param {number|object} props.sm - Grid width for small screens
 * @param {number|object} props.md - Grid width for medium screens
 * @param {number|object} props.lg - Grid width for large screens
 * @param {number|object} props.xl - Grid width for extra large screens
 * @param {string} props.direction - Grid direction (row, column, row-reverse, column-reverse)
 * @param {string} props.justifyContent - Grid justifyContent (flex-start, center, flex-end, space-between, space-around, space-evenly)
 * @param {string} props.alignItems - Grid alignItems (flex-start, center, flex-end, stretch, baseline)
 * @param {number|string} props.spacing - Spacing between grid items
 * @param {boolean} props.container - If true, the grid is a container
 * @param {boolean} props.item - If true, the grid is an item
 * @param {string} props.wrap - Grid wrap (nowrap, wrap, wrap-reverse)
 * @param {Object} props.sx - Additional styling for the grid
 * @param {Object} props.theme - Theme object for styling
 * @returns {JSX.Element}
 */
const CGrid = ({
  children,
  xs,
  sm,
  md,
  lg,
  xl,
  direction = "row",
  justifyContent = "flex-start",
  alignItems = "flex-start",
  spacing = 0,
  container = false,
  item = false,
  wrap = "wrap",
  sx = {},
  theme,
  ...rest
}) => {
  // Default styles based on theme (if provided)
  const defaultSx = theme
    ? {
        // Add any theme-specific default styling here
      }
    : {};

  return (
    <Grid
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      direction={direction}
      justifyContent={justifyContent}
      alignItems={alignItems}
      spacing={spacing}
      container={container}
      item={item}
      wrap={wrap}
      sx={{ ...defaultSx, ...sx }}
      {...rest}
    >
      {children}
    </Grid>
  );
};

CGrid.propTypes = {
  children: PropTypes.node,
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.bool]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.bool]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.bool]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.bool]),
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.bool]),
  direction: PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
  justifyContent: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly']),
  alignItems: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'stretch', 'baseline']),
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  container: PropTypes.bool,
  item: PropTypes.bool,
  wrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
  sx: PropTypes.object,
  theme: PropTypes.object,
};

export default CGrid;